import { useState } from 'react';

import { useNotification } from '@hooks/useNotification';

interface UseFileUploadOptions {
  maxSizeInMB?: number;
  allowedTypes?: string[];
}

interface UseFileUploadReturn {
  uploadFile: File | undefined;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  clearFile: () => void;
  resetFileInput: (inputElement: HTMLInputElement) => void;
}

export const useFileUpload = (
  options: UseFileUploadOptions = {}
): UseFileUploadReturn => {
  const { maxSizeInMB = 20, allowedTypes = ['.pdf'] } = options;
  const { showError } = useNotification();
  const [uploadFile, setUploadFile] = useState<File | undefined>(undefined);

  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

  const validateFile = (file: File): boolean => {
    // Check file size
    if (file.size > maxSizeInBytes) {
      showError(
        `File exceeds more than ${maxSizeInMB}MB. Please select a smaller file.`
      );
      return false;
    }

    // Check file type if allowedTypes is specified
    if (allowedTypes && allowedTypes.length > 0) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      const mimeType = file.type.toLowerCase();

      const isValidType = allowedTypes.some((type) =>
        type.startsWith('.')
          ? fileExtension === type.slice(1).toLowerCase()
          : mimeType.includes(type.toLowerCase())
      );

      if (!isValidType) {
        showError(
          `File type not allowed. Allowed types: ${allowedTypes.join(', ')}`
        );
        return false;
      }
    }

    return true;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUploadFile(undefined);

    const selectedFile = event.target.files?.[0];

    if (selectedFile) {
      if (validateFile(selectedFile)) {
        setUploadFile(selectedFile);
      } else {
        // Clear the input on validation failure
        event.target.value = '';
      }
    }
  };

  const clearFile = () => {
    setUploadFile(undefined);
  };

  const resetFileInput = (inputElement: HTMLInputElement) => {
    inputElement.value = '';
    setUploadFile(undefined);
  };

  return {
    uploadFile,
    handleFileChange,
    clearFile,
    resetFileInput,
  };
};
